import { FC, ReactElement, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { TAtoZMenuConfigs } from 'interfaces/generic';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { CenteredSpin } from 'components/AtoZMenuContent/Spin';
import { RegionsCollapseBySport } from 'components/AtoZMenuContent/SportListViews/LevelView/RegionsCollapseBySport';
import { EventCollapse } from 'components/EventCollapse';
import { GlobalIcon } from 'components/GlobalIcon';
import { FetchFavoriteMatchIds } from 'newcomponents/Shared/sportsbook/Favorite/FetchFavoriteMatchIds';
import { GoToLink } from 'newcomponents/Shared/sportsbook/GoToLink';
import { sortFavoriteSports } from 'hooks/sportsbook/useSortFavoriteSportsOrdering';
import { setScrollToCompetitionId } from 'store/actions';
import { getFavoriteSportIds } from 'store/selectors/fav-data';
import { getRegionDataLoading } from 'store/selectors/loadings';
import { getHasTopLeagueGames } from 'store/selectors/sport-data';
import {
  getIsLoggedIn,
  getPersonalisedDataLoaded
} from 'store/selectors/user-data';
import type { TClickHandler } from '../../SportPage';
import './index.less';

const getNotRealSportIconName = (sport: ENotRealSports) => {
  let result: string = sport;

  if (sport === ENotRealSports.betBuilder && SpringConfigs.IS_SGB_TYPE_FOR_BB) {
    result += 'sgp';
  }

  if (
    sport === ENotRealSports.olympicCompetition &&
    SpringConfigs.OLYMPIC_ICON_OTHER
  ) {
    result += 'other';
  }

  return result;
};

export const CenteredSpinnerWrapper = () => (
  <div className="sports-loading">
    <CenteredSpin className="sports-loading-spin" />
  </div>
);

type Props = {
  loading: boolean;
  data: Record<string, any>;
  clickHandler: TClickHandler;
  updateLoading?: (loading: boolean) => void;
  configs: TAtoZMenuConfigs;
};

export const LevelView: FC<Props> = ({
  data,
  loading,
  clickHandler,
  updateLoading,
  configs
}): ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isLoggedIn = useSelector(getIsLoggedIn);
  const favoriteSportIds = useSelector(getFavoriteSportIds);
  const hasTopLeagueGames = useSelector(getHasTopLeagueGames);
  const regionDataLoading = useSelector(getRegionDataLoading);
  const personalisedDataLoaded = useSelector(getPersonalisedDataLoaded);

  const levelViewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!regionDataLoading) {
      updateLoading?.(false);

      if (levelViewRef.current) {
        levelViewRef.current.style.overflow = 'auto';
      }
    }
  }, [regionDataLoading]);

  const goToItems = useMemo(() => {
    const items = [];

    if (
      SpringConfigs.IS_BOOSTED_ODDS_ENABLED &&
      configs.boostedOddsEnabledAToZ
    ) {
      items.push(ENotRealSports.boostedOdds);
    }

    if (SpringConfigs.TOP_LEAGUES && hasTopLeagueGames) {
      items.push(ENotRealSports.topLeague);
    }

    if (SpringConfigs.IS_MULTIPLES_ENABLED && configs.multiplesEnabledAToZ) {
      items.push(ENotRealSports.multiples);
    }

    if (SpringConfigs.OUTRIGHT_FILTER && configs.outRightEnabledAToZ) {
      items.push(ENotRealSports.outright);
    }

    if (SpringConfigs.TODAY_EVENTS && configs.todayEventsEnabledAToZ) {
      items.push(ENotRealSports.todayEvents);
    }

    if (SpringConfigs.TOP_MATCHES && configs.topMatchesEnabledAToZ) {
      items.push(ENotRealSports.topMatches);
    }

    if (
      SpringConfigs.OLYMPIC_COMPETITION &&
      configs.olympicCompetitionEnabledAtoZ
    ) {
      items.push(ENotRealSports.olympicCompetition);
    }

    if (SpringConfigs.BET_BUILDER_SWITCHER && configs.BBAToZ) {
      items.push(ENotRealSports.betBuilder);
    }

    if (SpringConfigs.COUPON_BUILDER_SWITCHER && configs.couponBuilderAToZ) {
      items.push(ENotRealSports.coupon);
    }

    return items;
  }, [hasTopLeagueGames]);

  const sortedSports = useMemo(() => {
    return sortFavoriteSports(
      favoriteSportIds,
      sortByOrder(Object.values(data)),
      'id'
    ) as Sport[];
  }, [data, sortByOrder, favoriteSportIds]);

  if (loading) {
    return CenteredSpinnerWrapper();
  }

  return (
    <div className="aTozLevelViewContainer" ref={levelViewRef}>
      {goToItems.map((item, index) => (
        <GoToLink
          key={index}
          title={t(`sportsbook.${item}`)}
          icon={
            <GlobalIcon
              size={EIconSize._24}
              lib="sports"
              name={getNotRealSportIconName(item)}
              className="text-color"
              prefix={{
                showAfterIconIsRendered: item === ENotRealSports.todayEvents
              }}
            />
          }
          onClick={() => {
            dispatch(setScrollToCompetitionId(undefined));
            clickHandler(`/${SpringConfigs.SPORTSBOOK_MOUNT_PATH}/${item}`);
          }}
          extra={
            <GlobalIcon
              lib="generic"
              name="caretDownSmall"
              theme="default"
              className={SpringConfigs.IS_RTL ? 'rotate__90' : 'rotate__270'}
              color="var(--v3-black-45)"
              size={EIconSize._24}
            />
          }
        />
      ))}
      <div className="aTozLevelViewContainer__sportTitle">
        {t('generic.aToZSport')}
      </div>
      {sortedSports.map(
        sport =>
          !!sport.game && (
            <EventCollapse
              style={{ borderBottom: '1px solid var(--v3-black-6)' }}
              title={sport?.name || ''}
              key={sport.alias}
              noMargin
              icon={
                <GlobalIcon
                  lib="sports"
                  name={sport.alias}
                  className="text-color"
                  size={EIconSize._24}
                />
              }
            >
              <RegionsCollapseBySport
                sportAlias={sport.alias}
                clickHandler={(alias: string) =>
                  clickHandler(alias, sport.alias, sport.type === 0)
                }
              />
            </EventCollapse>
          )
      )}

      {SpringConfigs.USER_PERSONALIZATION_ENABLED ? (
        isLoggedIn && personalisedDataLoaded ? (
          <FetchFavoriteMatchIds />
        ) : null
      ) : (
        <FetchFavoriteMatchIds />
      )}
    </div>
  );
};
